import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import ApiConstants from '../../constants/ApiConstants';
import xhr from '../../utils/api/xhr';

interface PaymentMethod {
  name: string;
}

export interface BillingHistoryItem {
  id: string | number;
  invoice_date: string;
  currency: string;
  sub_total: number;
  total_amount: number;
  payment_method: PaymentMethod;
  plan_name: string;
  period_start: number;
  period_end: number;
  status: string;
}

export interface UnpaidInvoice {
  id: string;
  invoice_number: string;
  amount: number;
  currency: string;
  due_date: string;
  total_amount: number;
  period_start: number;
  period_end: number;
}

interface BillingHistoryResponse {
  items: BillingHistoryItem[];
}

interface BillingHistoryItemResponse {
  item: BillingHistoryItem;
}

interface UnpaidInvoiceResponse {
  unpaid_invoice: UnpaidInvoice | null;
}

interface PayInvoiceResponse {
  message: string;
}

export const billingHistoryKeys = {
  all: ['billingHistory'] as const,
  list: () => [...billingHistoryKeys.all, 'list'] as const,
  detail: (id: string) => [...billingHistoryKeys.all, 'detail', id] as const,
  unpaidInvoice: () => [...billingHistoryKeys.all, 'unpaidInvoice'] as const,
};

async function fetchBillingHistory(): Promise<BillingHistoryResponse> {
  return xhr(ApiConstants.Base + 'billing-histories');
}

async function fetchBillingHistoryItem(id: string): Promise<BillingHistoryItemResponse> {
  return xhr(ApiConstants.Base + 'billing-histories/' + id);
}

async function fetchUnpaidInvoice(): Promise<UnpaidInvoiceResponse> {
  return xhr(ApiConstants.Base + 'billing-histories/unpaid-invoice');
}

async function payInvoice(billingHistoryId: string): Promise<PayInvoiceResponse> {
  return xhr(ApiConstants.Base + 'billing-histories/' + billingHistoryId + '/pay', {
    method: 'put',
  });
}

export function useBillingHistoryQuery() {
  return useQuery({
    queryKey: billingHistoryKeys.list(),
    queryFn: fetchBillingHistory,
    select: (data) => data.items,
  });
}

export function useBillingHistoryItemQuery(id: string | undefined) {
  return useQuery({
    queryKey: billingHistoryKeys.detail(id!),
    queryFn: () => fetchBillingHistoryItem(id!),
    enabled: !!id,
    select: (data) => data.item,
  });
}

export function useUnpaidInvoiceQuery() {
  return useQuery({
    queryKey: billingHistoryKeys.unpaidInvoice(),
    queryFn: fetchUnpaidInvoice,
    select: (data) => data.unpaid_invoice,
  });
}

export function usePayInvoiceMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: payInvoice,
    onSuccess: () => {
      // Invalidate billing history queries to refresh the data
      queryClient.invalidateQueries({ queryKey: billingHistoryKeys.all });
    },
  });
}
